<template>
  <div ref="rootEl" class="wrap">
    <TriggerButton
      :active="open || activeCount > 0"
      :compact="compact"
      :aria-expanded="open"
      aria-haspopup="dialog"
      title="Filters"
      @click="open = !open"
    >
      <AppIcon name="filter" :size="15" />
      <span v-if="activeCount > 0" class="badge">{{ activeCount }}</span>
    </TriggerButton>

    <div v-show="open" class="panel" role="dialog" aria-label="Filters">
      <div class="panelHead">
        <span class="panelTitle">Filters</span>
        <button v-if="activeCount > 0" type="button" class="clearAll" @click="$emit('clear')">
          Clear all
        </button>
      </div>
      <slot />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import AppIcon from '../../../components/AppIcon.vue'
import TriggerButton from './TriggerButton.vue'
import { useClickOutside } from '../../composables/useClickOutside'

/**
 * A badged funnel button with a filter popover — the shared shell every list
 * surface uses so filters look and behave the same app-wide. The host supplies
 * its own fields through the default slot as `<div class="field">` blocks with
 * a `.fieldLabel` caption (div, not label — a label re-dispatches clicks on
 * non-interactive dropdown content to the control and closes it); the badge
 * counts applied filters so nothing silently narrows a list while the panel
 * is shut.
 *
 * The panel is v-show, not v-if: an async SearchSelect inside caches its
 * selected option's label, and unmounting the panel would drop that cache —
 * the trigger would then render the raw id on reopen.
 */

defineProps<{
  activeCount: number
  compact?: boolean
}>()

defineEmits<{
  clear: []
}>()

const open = ref(false)
const rootEl = ref<HTMLElement | null>(null)
useClickOutside(rootEl, open)
</script>

<style scoped>
.wrap { position: relative; display: inline-flex; flex-shrink: 0; }

/* Trigger shell lives in TriggerButton.vue (shared with SortMenu). */
.badge {
  position: absolute;
  top: -5px;
  right: -5px;
  min-width: 15px;
  height: 15px;
  padding: 0 3px;
  border-radius: var(--radius-full);
  background: var(--color-accent);
  color: #fff;
  font-size: 10px;
  font-weight: var(--fw-semibold);
  line-height: 15px;
  text-align: center;
}

.panel {
  position: absolute;
  top: calc(100% + 6px);
  /* Opens down-and-left: the trigger usually sits rightmost on its row. Hosts
     with other geometry override via :deep(.panel). */
  right: 0;
  z-index: 40;
  width: 260px;
  padding: var(--space-3);
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  background: var(--color-bg-surface);
  border: 1px solid var(--color-border-subtle);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-md, 0 8px 24px rgba(0, 0, 0, 0.12));
}

.panelHead { display: flex; align-items: center; justify-content: space-between; }
.panelTitle {
  font-size: var(--fs-xs);
  font-weight: var(--fw-semibold);
  letter-spacing: var(--tracking-wide);
  text-transform: uppercase;
  color: var(--color-text-tertiary);
}
.clearAll {
  border: none;
  background: none;
  padding: 0;
  font-family: inherit;
  font-size: var(--fs-xs);
  color: var(--color-accent);
  cursor: pointer;
}
.clearAll:hover { text-decoration: underline; }

.panel :slotted(.field) { display: flex; flex-direction: column; gap: var(--space-1); }
.panel :slotted(.fieldLabel) { font-size: var(--fs-xs); color: var(--color-text-tertiary); }
</style>
